/*
 * App Actions
 *
 * Actions change things in your application
 * Since this boilerplate uses a uni-directional data flow, specifically redux,
 * we have these actions which are the only way your application interacts with
 * your application state. This guarantees that your state is up to date and nobody
 * messes it up weirdly somewhere.
 *
 * To add a new Action:
 * 1) Import your constant
 * 2) Add a function like this:
 *    export function yourAction(var) {
 *        return { type: YOUR_ACTION_CONSTANT, var: var }
 *    }
 */

import { EventTypes } from 'redux-segment';
import {
  GENERIC_ERROR,
  LOG_OUT,
  REPORT_TO_SEGMENT,
  ADD_SCREENING,
  AUDIENCE_BUILDER_VIEW,
  SURVEY_BUILDER_VIEW,
  ADD_THREE_QUESTIONS,
  ADD_QUESTION,
  ADD_MIN_MAX,
  QUESTIONNAIRE_UPDATE,
  ANSWER_CREATE,
  DELETE_MEDIA,
  TRIGGER_AUTOSAVE_REVISION,
  TRACK_ANALYTICS_EVENT,
  UNCONDITIONAL_HIDE_ANSWER,
  SHOW_GENERAL_NOTIFICATION,
} from 'containers/App/constants';

/**
 * Dispatched when loading the repositories fails
 *
 * @param  {object} error The error
 *
 * @return {object}       An action object with a type of LOAD_REPOS_ERROR passing the error
 */
export function logOut() {
  return {
    type: LOG_OUT,
  };
}

export function genericError(error) {
  return {
    type: GENERIC_ERROR,
    error,
  };
}

export function trackAnalyticsEvent(name) {
  return {
    type: TRACK_ANALYTICS_EVENT,
    name,
  };
}

export function trackEvent(name, payload) {
  return {
    type: name,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: name,
          properties: payload,
        },
      },
    },
  };
}

export function reportToSegment(userDetails) {
  return {
    type: REPORT_TO_SEGMENT,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: 'error_tracking_javascript',
          properties: {
            category: 'Errors',
            label: (userDetails && userDetails.id) || {},
            email: (userDetails && userDetails.email) || {},
          },
        },
      },
    },
  };
}

export function audienceBuilderViewSegment({
  eventPrefix = '',
  surveyId,
  email,
}) {
  return {
    type: AUDIENCE_BUILDER_VIEW,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}audience_builder_view`,
          properties: {
            email,
            s_id: surveyId,
          },
        },
      },
    },
  };
}

export function surveyBuilderViewSegment({
  eventPrefix = '',
  surveyId,
  email,
}) {
  return {
    type: SURVEY_BUILDER_VIEW,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}survey_builder_view`,
          properties: {
            s_id: surveyId,
            email,
          },
        },
      },
    },
  };
}

export function addScreeningToSegment({
  surveyId,
  questionType,
  eventPrefix = '',
  email,
}) {
  return {
    type: ADD_SCREENING,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}V1.Question.add.screening`,
          properties: {
            s_id: surveyId,
            qtype: questionType,
            email,
          },
        },
      },
    },
  };
}

export function addThreeQuestionsToSegment({
  surveyId,
  eventPrefix = '',
  email,
}) {
  return {
    type: ADD_THREE_QUESTIONS,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}survey_builder_create_3_questions`,
          properties: {
            s_id: surveyId,
            email,
          },
        },
      },
    },
  };
}

export function addQuestionToSegment({
  surveyId,
  email,
  type,
  eventPrefix = '',
}) {
  return {
    type: ADD_QUESTION,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}V1.Question.create`,
          properties: {
            s_id: surveyId,
            qtype: type,
            email,
          },
        },
      },
    },
  };
}

export function addMinMaxToSegment({ surveyId, email, type, eventPrefix }) {
  return {
    type: ADD_MIN_MAX,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}V1.Question.set.minmax`,
          properties: {
            s_id: surveyId,
            qtype: type,
            email,
          },
        },
      },
    },
  };
}

export function questionnaireUpdateToSegment({
  surveyId,
  email,
  type,
  eventPrefix = '',
}) {
  return {
    type: QUESTIONNAIRE_UPDATE,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}V1.Question.update`,
          properties: {
            s_id: surveyId,
            qtype: type,
            email,
          },
        },
      },
    },
  };
}

export function answerCreateToSegment({ surveyId, email, eventPrefix = '' }) {
  return {
    type: ANSWER_CREATE,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}V1.Answer.create`,
          properties: {
            s_id: surveyId,
            email,
          },
        },
      },
    },
  };
}

export function deleteMedia({ surveyId, refId, mediaType, side }) {
  return {
    type: DELETE_MEDIA,
    surveyId,
    refId,
    mediaType,
    side,
  };
}

export function triggerAutosaveRevision(surveyId) {
  return {
    type: TRIGGER_AUTOSAVE_REVISION,
    surveyId,
  };
}

export function addUnconditionalHideAnswerToSegment(payload) {
  const { eventPrefix = '', ...restData } = payload;
  return {
    type: UNCONDITIONAL_HIDE_ANSWER,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}add_unconditional_hide_answer`,
          properties: restData,
        },
      },
    },
  };
}
export function removeUnconditionalHideAnswerToSegment(payload) {
  const { eventPrefix = '', ...restData } = payload;
  return {
    type: UNCONDITIONAL_HIDE_ANSWER,
    meta: {
      analytics: {
        eventType: EventTypes.track,
        eventPayload: {
          event: `${eventPrefix}remove_unconditional_hide_answer`,
          properties: restData,
        },
      },
    },
  };
}

export function showGeneralNotification(payload) {
  return {
    type: SHOW_GENERAL_NOTIFICATION,
    payload,
  };
}
